import './table-hac-active.scss';
import React, {useEffect, useState} from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import apiClient from '../../../../utils/apiClient';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Alert, Snackbar } from '@mui/material';
import Grid from '@mui/material/Grid2';
import decodeToken from '../../../../utils/decode-token';
import RowActionsMenu from '../../common/table/row-actions-menu';
import {compareDmyDates, sortRows} from '../../../../utils/tableUtils';
import useSummarizedFilteredRows from '../../common/table/hooks/use-summarized-filtered-rows';
import handleEmptyTableResponse from '../../common/table/utils/handle-empty-table-response';

const SORT_COMPARATORS = {
  nomination_date: (a, b) => compareDmyDates(a.nomination_date, b.nomination_date),
};

function TableHacActive({reload, onDataLoaded, onSummaryChange}) {
  const {t} = useTranslation();
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRowsWithSummary] = useSummarizedFilteredRows(onSummaryChange);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [retailerOptions, setRetailerOptions] = useState([]);
  const [selectedRetailerId, setSelectedRetailerId] = useState('');
  const token = sessionStorage.getItem('token') || null;
  const role = decodeToken(token).role;
  const userId = decodeToken(token).id;
  const navigate = useNavigate();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      const authToken = token;
      const url = `${process.env.REACT_APP_API_URL}/hac/retailer-salesperson?status=active`;

      const config = {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Cache-Control': 'no-cache',
        },
      };

      try {
        const response = await apiClient.get(url, config);
        if (response.status === 200) {
          const sortedRows = sortRows(response.data, 'nomination_date', 'desc', SORT_COMPARATORS);

          const storedRetailerId = sessionStorage.getItem('filter-retailer');
          if (storedRetailerId) {
            const filtered = sortedRows.filter(row => row.retailer_info.id === Number(storedRetailerId));
            setFilteredRowsWithSummary(filtered);
            setSelectedRetailerId(storedRetailerId);
          } else {
            setFilteredRowsWithSummary(sortedRows);
          }

          setRows(sortedRows);
          onDataLoaded();
        } else if (response.status === 204) {
          handleEmptyTableResponse({
            clearStateSetters: [setRows, setFilteredRowsWithSummary],
            onDataLoaded,
          });
        }
      } catch (error) {
        if (error.response) {
          setSnackbarOpen(true);
          setSnackbarMessage(t('common-internal-server-error'), error);
        }
      } finally {
        setDataLoaded(true);
      }

      const userApiUrl = `${process.env.REACT_APP_API_URL}/retailer/staff-member?staffId=${userId}`;
      try {
        const userResponse = await apiClient.get(userApiUrl, config);
        setRetailerOptions(userResponse.data.retailerInfo || []);
      } catch (error) {
        console.error('Error al obtener datos del usuario:', error);
      }
    };

    if (reload) {
      fetchData();
    }
  }, [reload, token, onDataLoaded, t, userId, setFilteredRowsWithSummary]);

  const handleApplyFilter = () => {
    sessionStorage.setItem('filter-retailer', selectedRetailerId);
    if (selectedRetailerId) {
      const filtered = rows.filter(row => row.retailer_info.id === Number(selectedRetailerId));
      setFilteredRowsWithSummary(filtered);
    } else {
      setFilteredRowsWithSummary(rows);
    }
  };


  return (
    <>
      {dataLoaded ? (
        rows && rows.length !== 0 ? (
          <div>
            <div className="intro">
              <div className="text">
                <h4>{t('retailer-main-active-title')}</h4>
                <p>{t('retailer-main-active-text')}</p>
              </div>
            </div>
            {retailerOptions.length > 1 && (
              <div className="filters">
                <p>{t('common-search')}</p>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <div className="form-control">
                      <label htmlFor="retailer">{t('common-retailer')}</label>
                      <select
                        name="retailer"
                        id="retailer"
                        className="normal"
                        value={selectedRetailerId}
                        onChange={(e) => setSelectedRetailerId(e.target.value)}
                      >
                        <option value="">{t('common-all-retailers')}</option>
                        {retailerOptions.map((retailer) => (
                          <option key={retailer.id} value={retailer.id}>
                            {retailer.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <div className="actions">
                      <button className="btn" onClick={handleApplyFilter}>
                        {t('common-apply')}
                      </button>
                    </div>
                  </Grid>
                </Grid>
              </div>
            )}
            {filteredRows.length !== 0 ? (
              <div className="table-wrapper">
                <Table
                  aria-label="simple table"
                  className={`user-table retailer-active ${
                    role === 'retailerGM' ? 'retailerGM' : ''
                  }`}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell align="left"><span className="text">{t('common-name')}</span></TableCell>
                      <TableCell align="left"><span className="text">{t('common-last-name')}</span></TableCell>
                      <TableCell align="left"><span className="text">{t('common-phone')}</span></TableCell>
                      <TableCell align="left"><span className="text">{t('common-role-pma')}</span></TableCell>
                      {role === 'retailerGM' && (
                        <TableCell align="left"><span className="text">{t('common-retailer')}</span></TableCell>
                      )}
                      <TableCell align="left"><span className="text">{t('common-status')}</span></TableCell>
                      <TableCell align="left"><span className="text">{t('common-last-update')}</span></TableCell>
                      <TableCell align="left"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell align="left"><span className="text">{row.name}</span></TableCell>
                        <TableCell align="left"><span className="text">{row.last_name}</span></TableCell>
                        <TableCell align="left"><span className="text">{row.prefix_phone} {row.phone}</span></TableCell>
                        <TableCell align="left"><span className="text">{row.pma}</span></TableCell>
                        {role === 'retailerGM' && (
                          <TableCell align="left"><span
                            className="text">{row.retailer_info.name} ({row.retailer_info.city})</span></TableCell>
                        )}
                        <TableCell align="left">
                          <span className={row.login_status === 'ready' ? 'logged' : ''}>
                            {row.login_status === 'ready' ? t('common-logged') : t('common-not-logged')}
                          </span>
                        </TableCell>
                        <TableCell align="left"><span className="text">{row.last_update}</span></TableCell>
                        <TableCell align="center">
                          <RowActionsMenu
                            actions={[
                              {
                                key: 'profile',
                                label: t('common-profile'),
                                onClick: () =>
                                  navigate(`/common/personal-details/${row.id}`, {
                                    state: {
                                      breadcrumbName: row.name + ' ' + row.last_name,
                                    },
                                  }),
                              },
                              {
                                key: 'vehicles',
                                label: t('pma-hac-tab-option2'),
                                onClick: () =>
                                  navigate(`/common/vehicles/${row.id}`, {
                                    state: {
                                      breadcrumbName: row.name + ' ' + row.last_name,
                                    },
                                  }),
                              },
                              {
                                key: 'preferences',
                                label: t('pma-hac-tab-option3'),
                                onClick: () =>
                                  navigate(`/common/preferences/${row.id}`, {
                                    state: {
                                      breadcrumbName: row.name + ' ' + row.last_name,
                                    },
                                  }),
                              },
                              {
                                key: 'assigned-pmas',
                                label: t('admin-main-tab1-option5'),
                                onClick: () =>
                                  navigate(`/admin/assigned-pmas/${row.id}`, {
                                    state: {
                                      breadcrumbName: row.name + ' ' + row.last_name,
                                    },
                                  }),
                              },
                              {
                                key: 'shared-notes',
                                label: t('pma-hac-tab-option7'),
                                onClick: () =>
                                  navigate(`/retailer/shared-notes/${row.id}`, {
                                    state: {
                                      breadcrumbName: row.name + ' ' + row.last_name,
                                    },
                                  }),
                              },
                            ]}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="no-result">
                <h2 className="txt-center">
                  <small>{t('admin-main-tab1-text-empty')}</small>
                </h2>
              </div>
            )}
          </div>
        ) : (
          <div className="no-result">
            <h2 className="txt-center">
              {t('retailer-main-active-empty-title')}
              <small>{t('retailer-main-active-empty-text')}</small>
            </h2>
          </div>
        )
      ) : (
        <div>{t('common-loading')}</div>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
      >
        <Alert severity="error" sx={{width: '100%'}}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default TableHacActive;
